import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { api } from '../lib/api'
import { Btn, Field, Modal, Msg, inputCls, money, today, addDays } from './ui'
import { INVOICE_STATUSES, PAYMENT_TERMS, calcTotals, clientLabel, nextInvoiceNumber, type Client, type Invoice, type InvoiceItem, type Project, type Service } from '../lib/db'

interface Props { initial?: Invoice | null; clients: Client[]; presetClientId?: string; presetProjectId?: string; onClose: () => void; onSaved: (inv: Invoice) => void }

const blankItem = (n: number): InvoiceItem => ({ service_id: null, description: '', quantity: 1, unit_price: 0, amount: 0, sort_order: n })

export function InvoiceEditor({ initial, clients, presetClientId, presetProjectId, onClose, onSaved }: Props) {
  const [inv, setInv] = useState<Partial<Invoice>>(initial || { client_id: presetClientId || '', project_id: presetProjectId || '', status: 'brouillon', payment_terms: '30', due_date: addDays(today(), 30), notes: '' })
  const [items, setItems] = useState<InvoiceItem[]>([blankItem(0)])
  const [projects, setProjects] = useState<Project[]>([])
  const [services, setServices] = useState<Service[]>([])
  const [msg, setMsg] = useState(''); const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState<Invoice | null>(initial || null)
  const set = (k: keyof Invoice, v: any) => setInv(x => ({ ...x, [k]: v }))
  const client = clients.find(c => c.id === inv.client_id)

  useEffect(() => {
    supabase.from('services').select('*').eq('active', true).order('category').order('name').then(({ data }) => setServices((data as any) || []))
    if (initial?.id) supabase.from('invoice_items').select('*').eq('invoice_id', initial.id).order('sort_order').then(({ data }) => { if (data?.length) setItems(data as any) })
    else nextInvoiceNumber().then(n => setInv(x => ({ ...x, invoice_number: x.invoice_number || n })))
  }, [])
  useEffect(() => {
    if (!inv.client_id) { setProjects([]); return }
    supabase.from('projects').select('*').eq('client_id', inv.client_id).order('created_at', { ascending: false }).then(({ data }) => {
      const list = (data as any) || []; setProjects(list)
      if (!inv.project_id && list.length === 1) set('project_id', list[0].id)
    })
  }, [inv.client_id])

  const totals = calcTotals(items)
  const upd = (i: number, patch: Partial<InvoiceItem>) => setItems(list => list.map((it, j) => {
    if (j !== i) return it
    const n = { ...it, ...patch }; n.amount = Math.round((Number(n.quantity) || 0) * (Number(n.unit_price) || 0) * 100) / 100; return n
  }))
  const addService = (id: string) => {
    const s = services.find(x => x.id === id); if (!s) return
    setItems(list => {
      const base = list.length === 1 && !list[0].description && !list[0].unit_price ? [] : list
      return [...base, { service_id: s.id, description: s.name + (s.description ? ' · ' + s.description : ''), quantity: 1, unit_price: Number(s.default_price) || 0, amount: Number(s.default_price) || 0, sort_order: base.length }]
    })
  }
  const onTerms = (t: string) => { set('payment_terms', t); set('due_date', addDays(today(), t === 'reception' ? 0 : Number(t))) }

  async function quickProject() {
    if (!client) return
    const { data, error } = await supabase.from('projects').insert({ client_id: client.id, title: `Projet ${clientLabel(client)}`, status: 'planifié' }).select().single()
    if (error) { setMsg('Erreur : ' + error.message); return }
    setProjects(p => [data as any, ...p]); set('project_id', (data as any).id)
  }

  async function save(): Promise<Invoice | null> {
    setBusy(true); setMsg('')
    if (!inv.client_id || !inv.project_id) { setMsg('Erreur : client et projet requis (créez un projet rapide si besoin).'); setBusy(false); return null }
    const clean = items.filter(i => i.description.trim() || Number(i.unit_price))
    if (!clean.length) { setMsg('Erreur : ajoutez au moins une ligne.'); setBusy(false); return null }
    const payload = { client_id: inv.client_id, project_id: inv.project_id, invoice_number: inv.invoice_number || null, status: inv.status || 'brouillon', due_date: inv.due_date || null,
      paid_date: inv.status === 'payée' ? (inv.paid_date || today()) : (inv.paid_date || null), notes: inv.notes || null, payment_terms: inv.payment_terms || 'reception',
      subtotal: totals.subtotal, tps_amount: totals.tps, tvq_amount: totals.tvq, tax_amount: Math.round((totals.tps + totals.tvq) * 100) / 100, amount: totals.total }
    const q = inv.id ? supabase.from('invoices').update(payload).eq('id', inv.id) : supabase.from('invoices').insert(payload)
    const { data, error } = await q.select().single()
    if (error) { setMsg('Erreur : ' + error.message); setBusy(false); return null }
    const saved = data as Invoice
    await supabase.from('invoice_items').delete().eq('invoice_id', saved.id)
    const { error: e2 } = await supabase.from('invoice_items').insert(clean.map((it, i) => ({ invoice_id: saved.id, service_id: it.service_id, description: it.description.trim(), quantity: Number(it.quantity) || 0, unit_price: Number(it.unit_price) || 0, amount: it.amount, sort_order: i })))
    setBusy(false)
    if (e2) { setMsg('Erreur lignes : ' + e2.message); return null }
    setInv(saved); setSaved(saved); setMsg('✅ Facture enregistrée.'); onSaved(saved)
    return saved
  }

  async function send() {
    const s = await save(); if (!s || !client) return
    setBusy(true)
    const link = `${window.location.origin}/factures/${s.id}`
    const lines = items.filter(i => i.description.trim()).map(i => `- ${i.description.trim()} : ${i.quantity} × ${money(i.unit_price)} = ${money(i.amount)}`).join('\n')
    const body = `Bonjour ${client.full_name},\n\nVoici votre facture ${s.invoice_number || ''} de Pivot Média.\n\n${lines}\n\nSous-total : ${money(totals.subtotal)}\nTPS (5 %) : ${money(totals.tps)}\nTVQ (9,975 %) : ${money(totals.tvq)}\nTotal : ${money(totals.total)}\n\nÉchéance : ${s.due_date || 'à réception'}\n\nVous pouvez consulter et imprimer la facture ici : ${link}\n\nMerci de votre confiance,\nPivot Média\ninfo@pivot-media.ca`
    try {
      await api('/send-email', { method: 'POST', json: { to: client.email, subject: `Facture ${s.invoice_number || ''} · Pivot Média`, body } })
      if (s.status === 'brouillon') { await supabase.from('invoices').update({ status: 'envoyée' }).eq('id', s.id); set('status', 'envoyée'); onSaved({ ...s, status: 'envoyée' }) }
      setMsg('✅ Facture envoyée à ' + client.email)
    } catch (e: any) { setMsg('Erreur envoi : ' + e.message) } finally { setBusy(false) }
  }

  return (
    <Modal title={inv.id ? `Facture ${inv.invoice_number || ''}` : 'Nouvelle facture'} onClose={onClose} wide>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Field label="Client *" className="lg:col-span-2">
          <select className={inputCls} value={inv.client_id || ''} onChange={e => { set('client_id', e.target.value); set('project_id', '') }} disabled={!!inv.id}>
            <option value="">— Choisir —</option>{clients.map(c => <option key={c.id} value={c.id}>{clientLabel(c)}</option>)}
          </select>
        </Field>
        <Field label="Projet *" className="lg:col-span-2">
          <div className="flex gap-2">
            <select className={inputCls} value={inv.project_id || ''} onChange={e => set('project_id', e.target.value)} disabled={!inv.client_id}>
              <option value="">— Choisir —</option>{projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
            {inv.client_id && !projects.length && <Btn type="button" variant="ghost" small onClick={quickProject}>+ Projet</Btn>}
          </div>
        </Field>
        <Field label="Numéro"><input className={inputCls} value={inv.invoice_number || ''} onChange={e => set('invoice_number', e.target.value)} /></Field>
        <Field label="Conditions"><select className={inputCls} value={inv.payment_terms || 'reception'} onChange={e => onTerms(e.target.value)}>{PAYMENT_TERMS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
        <Field label="Échéance"><input type="date" className={inputCls} value={inv.due_date || ''} onChange={e => set('due_date', e.target.value)} /></Field>
        <Field label="Statut"><select className={inputCls} value={inv.status || 'brouillon'} onChange={e => set('status', e.target.value)}>{INVOICE_STATUSES.map(s => <option key={s}>{s}</option>)}</select></Field>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-xl tracking-wide">Lignes</h3>
        <div className="flex gap-2">
          <select className={inputCls + ' w-auto'} value="" onChange={e => addService(e.target.value)}>
            <option value="">+ Ajouter un service du catalogue</option>
            {services.map(s => <option key={s.id} value={s.id}>{s.name} · {money(s.default_price)}</option>)}
          </select>
          <Btn type="button" variant="ghost" small onClick={() => setItems(l => [...l, blankItem(l.length)])}>+ Ligne libre</Btn>
        </div>
      </div>
      <div className="mt-3 border border-white/10 rounded overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-white/5 text-acier text-[11px] uppercase tracking-widest"><tr><th className="text-left px-3 py-2">Description</th><th className="px-3 py-2 w-20">Qté</th><th className="px-3 py-2 w-32">Prix unit.</th><th className="text-right px-3 py-2 w-28">Montant</th><th className="w-10" /></tr></thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className="border-t border-white/5">
                <td className="px-2 py-1"><input className={inputCls} value={it.description} onChange={e => upd(i, { description: e.target.value })} placeholder="Description" /></td>
                <td className="px-2 py-1"><input type="number" step="0.5" className={inputCls + ' text-center'} value={it.quantity} onChange={e => upd(i, { quantity: +e.target.value })} /></td>
                <td className="px-2 py-1"><input type="number" step="0.01" className={inputCls + ' text-right'} value={it.unit_price} onChange={e => upd(i, { unit_price: +e.target.value })} /></td>
                <td className="px-3 py-1 text-right whitespace-nowrap">{money(it.amount)}</td>
                <td className="px-2 py-1 text-center"><button type="button" onClick={() => setItems(l => l.filter((_, j) => j !== i))} className="text-acier hover:text-red-300">×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-6">
        <Field label="Notes sur la facture (visibles par le client)"><textarea className={inputCls + ' min-h-[100px]'} value={inv.notes || ''} onChange={e => set('notes', e.target.value)} placeholder="Modalités de paiement, virement Interac à info@pivot-media.ca, etc." /></Field>
        <div className="text-sm space-y-1 sm:text-right">
          <div className="flex sm:justify-end gap-6"><span className="text-acier">Sous-total</span><span className="w-28 text-right">{money(totals.subtotal)}</span></div>
          <div className="flex sm:justify-end gap-6"><span className="text-acier">TPS 5 %</span><span className="w-28 text-right">{money(totals.tps)}</span></div>
          <div className="flex sm:justify-end gap-6"><span className="text-acier">TVQ 9,975 %</span><span className="w-28 text-right">{money(totals.tvq)}</span></div>
          <div className="flex sm:justify-end gap-6 font-display text-2xl text-orange pt-2"><span>Total</span><span className="w-28 text-right">{money(totals.total)}</span></div>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Btn onClick={save} disabled={busy}>{busy ? 'Enregistrement…' : 'Enregistrer'}</Btn>
        <Btn variant="ghost" onClick={send} disabled={busy || !client}>✉ Enregistrer et envoyer</Btn>
        {saved?.id && <a href={`/factures/${saved.id}`} target="_blank" rel="noreferrer" className="px-5 py-2.5 rounded-sm text-xs font-bold uppercase tracking-widest bg-white/5 hover:bg-white/10 border border-white/10">🖨 Imprimer / PDF</a>}
        <Btn variant="ghost" onClick={onClose}>Fermer</Btn>
      </div>
      <Msg msg={msg} />
    </Modal>
  )
}
